import { theme as antdTheme } from 'antd';

// ─── Brand Colors ───────────────────────────────────────────────────────────
export const colors = {
  // Primary palette
  primary: '#F62E97',        // Persian Rose — main accent
  primaryDark: '#94167F',    // Mardi Gras — buttons, borders
  primaryDeep: '#300350',    // Russian Violet — deep accents
  secondary: '#153CB4',      // Persian Blue — gradient end
  accent: '#E93479',         // Cerise Pink
  warm: '#F9AC53',           // Rajah — warm highlights

  // Surfaces (v2 ramp: base → content → elevated)
  bgBase: '#0E0A1A',        // Deepest background (body/html)
  bgContent: '#15122E',     // Main content / scroll areas
  bgElevated: '#1C1838',    // Cards, modals, inputs, elevated surfaces
  bgNavbar: '#0A0718',      // Navbar / header
  bgSider: '#0A0718',       // Sidebar
  bgInput: '#1C1838',       // Input fields (match elevated)
  bgFooter: '#0A0718',      // Footer

  // Text
  textPrimary: '#F0EDF5',   // Main text — slightly warm white
  textSecondary: '#A39EAE', // Muted labels (improved vs #15122E)
  textTertiary: '#6B6478',  // Disabled / hint text
  textInverse: '#ffffff',   // Text on colored backgrounds

  // Borders
  border: 'rgba(255, 255, 255, 0.10)',
  borderLight: 'rgba(255, 255, 255, 0.06)',
  borderAccent: 'rgba(148, 22, 127, 0.35)',

  // Status
  success: '#52C41A',
  successBg: 'rgba(57, 255, 20, 0.1)',
  successBorder: 'rgba(57, 255, 20, 0.3)',
  warning: '#FADB14',
  warningBg: 'rgba(250, 219, 20, 0.1)',
  warningBorder: 'rgba(250, 219, 20, 0.35)',
  error: '#FF4D4F',
  errorBg: 'rgba(255, 77, 79, 0.1)',
  errorBorder: 'rgba(255, 77, 79, 0.3)',
  processing: '#1890FF',

  // Scrollbar
  scrollTrack: '#15122E',
  scrollThumb: '#94167F',
  scrollThumbHover: '#a51d8f',

  /** Project directory cards / badges (shared layout; theme swaps colors) */
  projectCardBg: 'linear-gradient(145deg, rgba(246, 46, 151, 0.42) 0%, rgba(148, 22, 127, 0.28) 42%, rgba(48, 3, 80, 0.55) 100%)',
  projectCardBorder: 'rgba(255, 255, 255, 0.08)',
  projectCardShadow: '0 1px 2px rgba(0, 0, 0, 0.45)',
  projectCardHoverShadow: '0 8px 28px rgba(0, 0, 0, 0.45), 0 2px 10px rgba(0, 0, 0, 0.3)',
  projectCardHoverBorder: 'rgba(255, 255, 255, 0.14)',
  projectChatBtnBg: 'rgba(255, 255, 255, 0.06)',
  projectChatBtnBorder: 'rgba(255, 255, 255, 0.1)',
  badgeDeployedBg: 'rgba(6, 78, 59, 0.55)',
  badgeDeployedFg: '#4ADE80',
  badgeDeployedBorder: 'rgba(74, 222, 128, 0.28)',
  badgeNotDeployedBg: 'rgba(120, 53, 15, 0.5)',
  badgeNotDeployedFg: '#FCD34D',
  badgeNotDeployedBorder: 'rgba(252, 211, 77, 0.28)',

  // Legacy aliases (backward compat)
  russianViolet: '#300350',
  mardiGras: '#94167F',
  cerisePink: '#E93479',
  rajah: '#F9AC53',
  persianRose: '#F62E97',
  persianBlue: '#153CB4',
};

// ─── Typography ─────────────────────────────────────────────────────────────
export const fonts = {
  // Primary: clean, modern sans-serif for UI text
  primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  // Headings: slightly bolder geometric sans
  heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  // Monospace: for code, IDs, collection names, technical data
  mono: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Overpass Mono', 'Consolas', monospace",
};

// ─── Gradients ──────────────────────────────────────────────────────────────
export const gradients = {
  brand: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
  brandSubtle: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.secondary} 100%)`,
  cardBg: `linear-gradient(135deg, rgba(148, 22, 127, 0.08) 0%, rgba(21, 60, 180, 0.08) 100%)`,
  cardBorder: `linear-gradient(135deg, rgba(148, 22, 127, 0.4) 0%, rgba(21, 60, 180, 0.4) 100%)`,
  divider: `linear-gradient(90deg, rgba(148, 22, 127, 0.5) 0%, rgba(21, 60, 180, 0.5) 100%)`,
};

// ─── Shadows ────────────────────────────────────────────────────────────────
export const shadows = {
  sm: '0 1px 4px rgba(0, 0, 0, 0.25)',
  md: '0 2px 8px rgba(0, 0, 0, 0.3)',
  lg: '0 4px 16px rgba(0, 0, 0, 0.4)',
  /** Modals / drawers — depth without competing with brand */
  elevation: '0 8px 24px rgba(0, 0, 0, 0.45)',
  elevationLight: '0 8px 24px rgba(15, 15, 30, 0.12)',
  /** Light-mode cards on white base */
  cardLight: '0 1px 2px rgba(15, 15, 30, 0.06)',
  glow: '0 0 20px rgba(148, 22, 127, 0.3)',
  glowStrong: '0 0 30px rgba(148, 22, 127, 0.5)',
  textGlow: '0 0 10px rgba(255, 255, 255, 0.1), 0 0 20px rgba(255, 255, 255, 0.06)',
};

// ─── Spacing / Radius ───────────────────────────────────────────────────────
export const radius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  pill: '999px',
};

/** Standard outer padding for authenticated app pages */
export const PAGE_PADDING = '18px';
export const pageShellStyle = {
  padding: PAGE_PADDING,
  height: '100%',
  boxSizing: 'border-box',
};

// ─── Ant Design Theme Config ────────────────────────────────────────────────
export const darkTheme = {
  token: {
    colorPrimary: colors.primary,
    colorBgContainer: colors.bgElevated,
    colorBgElevated: colors.bgElevated,
    colorBgBase: colors.bgContent,
    colorText: colors.textPrimary,
    colorTextSecondary: colors.textSecondary,
    colorTextTertiary: colors.textTertiary,
    colorTextQuaternary: colors.textTertiary,
    colorBorder: colors.border,
    colorBorderSecondary: colors.borderLight,
    fontFamily: fonts.primary,
    fontFamilyCode: fonts.mono,
    borderRadius: 8,
    fontSize: 13,
    fontSizeSM: 12,
    fontSizeLG: 15,
    fontSizeXL: 18,
    fontSizeHeading1: 26,
    fontSizeHeading2: 22,
    fontSizeHeading3: 18,
    fontSizeHeading4: 15,
    fontSizeHeading5: 13,
    lineHeight: 1.6,
    fontWeightStrong: 600,
    colorTextLightSolid: '#ffffff',
    controlOutline: 'rgba(246, 46, 151, 0.35)',
  },
  algorithm: antdTheme.darkAlgorithm,
  components: {
    Card: {
      colorBgContainer: 'transparent',
    },
    Table: {
      colorBgContainer: 'transparent',
      /** Header slightly elevated vs #0F172A body (slate) for clear hierarchy */
      headerBg: '#1E293B',
      fontSize: 13,
      headerColor: '#E2E8F0',
      colorText: colors.textPrimary,
      rowHoverBg: 'rgba(255, 255, 255, 0.04)',
      borderColor: 'rgba(255, 255, 255, 0.08)',
      headerSplitColor: 'rgba(255, 255, 255, 0.06)',
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },
    Descriptions: {
      colorBgContainer: 'transparent',
      fontSize: 13,
      labelBg: 'rgba(148, 22, 127, 0.06)',
      contentColor: colors.textPrimary,
      labelColor: colors.textSecondary,
    },
    Steps: {
      colorPrimary: colors.primary,
      colorText: colors.textPrimary,
      fontSize: 13,
    },
    Button: {
      colorPrimary: colors.primary,
      primaryColor: colors.textInverse,
      fontSize: 13,
      fontWeight: 500,
    },
    Layout: {
      bodyBg: colors.bgContent,
      headerBg: colors.bgNavbar,
      siderBg: colors.bgSider,
    },
    Input: {
      colorBgContainer: colors.bgInput,
      fontSize: 13,
    },
    Select: {
      colorBgContainer: colors.bgInput,
      fontSize: 13,
    },
    Modal: {
      contentBg: colors.bgElevated,
      headerBg: colors.bgElevated,
      titleFontSize: 16,
      fontSize: 13,
      boxShadow: shadows.elevation,
    },
    Drawer: {
      colorBgElevated: colors.bgElevated,
      boxShadow: shadows.elevation,
    },
    Tabs: {
      fontSize: 13,
      titleFontSize: 13,
      itemColor: colors.textSecondary,
      itemActiveColor: colors.textPrimary,
      itemSelectedColor: colors.primary,
      inkBarColor: colors.primary,
    },
    Form: {
      fontSize: 13,
      labelColor: colors.textSecondary,
      labelFontSize: 13,
    },
    Tag: {
      fontSize: 12,
      fontSizeSM: 11,
    },
    Collapse: {
      fontSize: 13,
      headerBg: 'transparent',
      contentBg: 'transparent',
    },
    Typography: {
      colorText: colors.textPrimary,
      colorTextSecondary: colors.textSecondary,
      fontWeightStrong: 600,
    },
    Tooltip: {
      fontSize: 12,
    },
    Pagination: {
      fontSize: 13,
    },
    Switch: {
      colorPrimary: colors.primary,
    },
    Spin: {
      colorPrimary: colors.primary,
    },
  },
};

// Default export for backward compatibility
export const theme = darkTheme;

/** Light palette for Ant Design + inline styles when theme mode is `light`. */
export const lightColors = {
  primary: '#C4156E',
  primaryDark: '#94167F',
  primaryDeep: '#300350',
  secondary: '#153CB4',
  accent: '#E93479',
  warm: '#D97706',
  bgBase: '#F3F4F8',
  /** Main scroll surface — neutral white (no pink/lavender cast; cards use borders for separation) */
  bgContent: '#FFFFFF',
  bgElevated: '#FFFFFF',
  /** Match navbar + sidebar for a unified shell (light theme only) */
  bgNavbar: '#FFFFFF',
  bgSider: '#FFFFFF',
  bgInput: '#FFFFFF',
  bgFooter: '#EEF1F6',
  textPrimary: '#14141F',
  textSecondary: '#5E6270',
  textTertiary: '#8B8FA3',
  textInverse: '#FFFFFF',
  border: 'rgba(15, 15, 30, 0.12)',
  borderLight: 'rgba(15, 15, 30, 0.06)',
  borderAccent: 'rgba(148, 22, 127, 0.35)',
  success: '#389E0D',
  successBg: 'rgba(82, 196, 26, 0.12)',
  successBorder: 'rgba(82, 196, 26, 0.35)',
  warning: '#D48806',
  warningBg: 'rgba(250, 173, 20, 0.15)',
  warningBorder: 'rgba(250, 173, 20, 0.45)',
  error: '#CF1322',
  errorBg: 'rgba(255, 77, 79, 0.08)',
  errorBorder: 'rgba(255, 77, 79, 0.35)',
  processing: '#1677FF',
  scrollTrack: '#E8EAEF',
  scrollThumb: '#94167F',
  scrollThumbHover: '#b51d96',

  /** Project directory cards / badges */
  projectCardBg: '#FFFFFF',
  projectCardBorder: '#E5E7EB',
  projectCardShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  projectCardHoverShadow: '0 8px 24px rgba(15, 15, 30, 0.12), 0 2px 8px rgba(15, 15, 30, 0.06)',
  projectCardHoverBorder: '#D1D5DB',
  projectChatBtnBg: '#FAFAFA',
  projectChatBtnBorder: '#E5E7EB',
  badgeDeployedBg: '#DCFCE7',
  badgeDeployedFg: '#15803D',
  badgeDeployedBorder: '#BBF7D0',
  badgeNotDeployedBg: '#FEF2F2',
  badgeNotDeployedFg: '#B91C1C',
  badgeNotDeployedBorder: '#FECACA',

  russianViolet: '#300350',
  mardiGras: '#94167F',
  cerisePink: '#E93479',
  rajah: '#D97706',
  persianRose: '#C4156E',
  persianBlue: '#153CB4',
};

export const lightTheme = {
  token: {
    colorPrimary: lightColors.primary,
    colorBgContainer: lightColors.bgElevated,
    colorBgElevated: lightColors.bgElevated,
    colorBgBase: lightColors.bgContent,
    colorText: lightColors.textPrimary,
    colorTextSecondary: lightColors.textSecondary,
    colorTextTertiary: lightColors.textTertiary,
    colorTextQuaternary: lightColors.textTertiary,
    colorBorder: lightColors.border,
    colorBorderSecondary: lightColors.borderLight,
    fontFamily: fonts.primary,
    fontFamilyCode: fonts.mono,
    borderRadius: 8,
    fontSize: 13,
    fontSizeSM: 12,
    fontSizeLG: 15,
    fontSizeXL: 18,
    fontSizeHeading1: 26,
    fontSizeHeading2: 22,
    fontSizeHeading3: 18,
    fontSizeHeading4: 15,
    fontSizeHeading5: 13,
    lineHeight: 1.6,
    fontWeightStrong: 600,
    colorTextLightSolid: '#ffffff',
    controlOutline: 'rgba(196, 21, 110, 0.35)',
  },
  algorithm: antdTheme.defaultAlgorithm,
  components: {
    ...darkTheme.components,
    Card: {
      colorBgContainer: 'transparent',
    },
    Table: {
      colorBgContainer: 'transparent',
      headerBg: '#F3F4F6',
      fontSize: 13,
      headerColor: '#111827',
      colorText: lightColors.textPrimary,
      rowHoverBg: '#F9FAFB',
      borderColor: '#E5E7EB',
      headerSplitColor: '#E5E7EB',
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
    },
    Layout: {
      bodyBg: lightColors.bgContent,
      headerBg: lightColors.bgNavbar,
      siderBg: lightColors.bgSider,
    },
    Input: {
      colorBgContainer: lightColors.bgInput,
      fontSize: 13,
    },
    Select: {
      colorBgContainer: lightColors.bgInput,
      fontSize: 13,
    },
    Modal: {
      contentBg: lightColors.bgElevated,
      headerBg: lightColors.bgElevated,
      titleFontSize: 16,
      fontSize: 13,
      boxShadow: shadows.elevationLight,
    },
    Drawer: {
      colorBgElevated: lightColors.bgElevated,
      boxShadow: shadows.elevationLight,
    },
    Segmented: {
      trackBg: '#F3F4F6',
      itemColor: lightColors.textSecondary,
      itemHoverColor: lightColors.textPrimary,
      itemHoverBg: 'rgba(20, 20, 31, 0.06)',
      itemSelectedBg: lightColors.textPrimary,
      itemSelectedColor: '#FFFFFF',
      itemActiveBg: 'rgba(20, 20, 31, 0.08)',
    },
    Button: {
      ...(darkTheme.components?.Button || {}),
      colorPrimary: lightColors.primary,
      primaryColor: lightColors.textInverse,
      defaultColor: lightColors.textPrimary,
      defaultBorderColor: lightColors.border,
      fontSize: 13,
      fontWeight: 500,
    },
    /**
     * Override dark Steps tokens from spread — especially `colorText`, which becomes
     * finish-title color and was near-white on light backgrounds (unreadable ticked labels).
     */
    Steps: {
      colorPrimary: lightColors.primary,
      colorText: lightColors.textPrimary,
      colorTextDescription: lightColors.textSecondary,
      colorTextDisabled: lightColors.textTertiary,
      fontSize: 13,
    },
    /** Bordered label column: neutral gray (dark spread used mauve `labelBg`, which reads as pink on white). */
    Descriptions: {
      ...(darkTheme.components?.Descriptions || {}),
      colorBgContainer: 'transparent',
      fontSize: 13,
      labelBg: '#F3F4F6',
      contentColor: lightColors.textPrimary,
    },
  },
};

// ─── Reusable Style Objects ─────────────────────────────────────────────────
export const customStyles = {
  brandGradient: gradients.brand,
  brandGradientText: {
    background: gradients.brand,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
  },
  // Common button style
  primaryButton: {
    background: gradients.brandSubtle,
    border: 'none',
    boxShadow: shadows.glow,
  },
  // Card with gradient border
  gradientCard: {
    background: gradients.cardBg,
    border: `1px solid ${colors.primaryDark}`,
    borderRadius: radius.md,
  },
  // Section divider
  divider: {
    height: '1px',
    background: gradients.divider,
  },
  // Page title glow
  titleGlow: {
    textShadow: shadows.textGlow,
  },
  // Status badge styles
  deployedBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: fonts.mono,
    background: colors.successBg,
    border: `0.6px solid ${colors.successBorder}`,
    color: colors.success,
    whiteSpace: 'nowrap',
  },
  notDeployedBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    fontFamily: fonts.mono,
    background: colors.warningBg,
    border: `0.6px solid ${colors.warningBorder}`,
    color: colors.warning,
    whiteSpace: 'nowrap',
  },
  // Info box (used in create/deploy steps)
  infoBox: {
    padding: '12px',
    background: 'rgba(148, 22, 127, 0.06)',
    borderRadius: '6px',
    borderLeft: `3px solid ${colors.primaryDark}`,
  },
  infoBoxText: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: '14px',
    lineHeight: '1.6',
  },
};

// ─── Theme-aware Styles (backward compat) ───────────────────────────────────
/**
 * @param {'dark' | 'light'} [mode='dark']
 */
export const getThemeStyles = (mode = 'dark') => {
  const c = mode === 'light' ? lightColors : colors;
  return {
    mode,
    isLight: mode === 'light',
    /** Layout chrome dividers (light: neutral gray per SaaS shell spec) */
    shellDivider: mode === 'light' ? '#E5E7EB' : c.border,
    navbarBg: c.bgNavbar,
    contentBg: c.bgContent,
    elevatedBg: c.bgElevated,
    inputBg: c.bgInput,
    siderBg: c.bgSider,
    footerBg: c.bgFooter,
    footerMuted:
      mode === 'light' ? 'rgba(20, 20, 31, 0.58)' : 'rgba(240, 237, 245, 0.68)',
    textPrimary: c.textPrimary,
    textSecondary: c.textSecondary,
    textTertiary: c.textTertiary,
    cardBg: 'transparent',
    borderColor: c.border,
    borderColorLight: c.borderLight,
    borderAccent: c.borderAccent,
    projectCardBg: c.projectCardBg,
    projectCardBorder: c.projectCardBorder,
    projectCardShadow: c.projectCardShadow,
    projectCardHoverShadow: c.projectCardHoverShadow,
    projectCardHoverBorder: c.projectCardHoverBorder,
    projectChatBtnBg: c.projectChatBtnBg,
    projectChatBtnBorder: c.projectChatBtnBorder,
    badgeDeployedBg: c.badgeDeployedBg,
    badgeDeployedFg: c.badgeDeployedFg,
    badgeDeployedBorder: c.badgeDeployedBorder,
    badgeNotDeployedBg: c.badgeNotDeployedBg,
    badgeNotDeployedFg: c.badgeNotDeployedFg,
    badgeNotDeployedBorder: c.badgeNotDeployedBorder,
    /** Super admin user management table */
    userTableZebraA: mode === 'light' ? '#FFFFFF' : '#0F172A',
    userTableZebraB: mode === 'light' ? '#FAFAFA' : '#111827',
    userTableRowHoverBg: mode === 'light' ? '#F3F4F6' : 'rgba(255, 255, 255, 0.05)',
    userTableHeaderBg: mode === 'light' ? '#F3F4F6' : '#1E293B',
    userTableHeaderBorder: mode === 'light' ? '#E5E7EB' : 'rgba(255, 255, 255, 0.1)',
    shadow: shadows.md,
    shadowHover: shadows.lg,
    shadowSm: shadows.sm,
    shadowElevation: mode === 'light' ? shadows.elevationLight : shadows.elevation,
    shadowCard: mode === 'light' ? shadows.cardLight : shadows.sm,
  };
};
